// File: noviopus-ats-client/src/features/Auth/selectors.ts
import { createSelector } from 'reselect';
import { List, Iterable } from 'immutable';
import { initialState as authInitialState } from './authSlice';
import { ADMIN_PERMISSION } from '@/Permissions/knownPermissions';

// --- SAFE EXTRACTOR ---
// Extracts 'auth' from Root State, handling both Immutable Map and POJO Root
const getAuthSlice = (state: any) => {
    let auth = null;

    // 1. Try Immutable .get()
    if (state && typeof state.get === 'function') {
        auth = state.get('auth');
    } 
    // 2. Try POJO property access
    else if (state && state.auth) {
        auth = state.auth;
    }

    if (!auth) return authInitialState;
    
    // 3. Normalize the Slice itself
    // If the slice was stored as Immutable (legacy), convert to JS immediately.
    // If it's already POJO (new authSlice), return as is.
    if (auth && typeof auth.toJS === 'function') {
        return auth.toJS();
    }
    return auth;
};

// --- PRIMITIVE SELECTORS (Pure JS Output) ---

export const selectIsAuth = (state: any) => getAuthSlice(state).isAuth;

export const selectIsAuthLoading = (state: any) => getAuthSlice(state).isLoading;

export const selectIsInitialCheckComplete = (state: any) => getAuthSlice(state).isInitialCheckComplete;

export const selectAuthUser = (state: any) => {
    const slice = getAuthSlice(state);
    return slice.authUser || slice.currentUser || null;
};

export const selectCurrentAccount = (state: any) => getAuthSlice(state).currentAccount;

// --- ALIASES ---
export const getCurrentUser = selectAuthUser;
export const getCurrentAccount = selectCurrentAccount;

// --- UTILITY SELECTORS ---

export const selectToken = createSelector(
  selectAuthUser,
  (authUser) => authUser?.token || authUser?.id
);

export const selectUserEmail = createSelector(
  selectAuthUser,
  (authUser) => authUser?.email
);

// Helper for Legacy Immutable Companies
export const getCurrentCompanies = (state: any) => {
    // Companies is still legacy Immutable
    const companies = state && typeof state.get === 'function' ? state.get('companies') : state?.companies;
    const list = companies && typeof companies.get === 'function' ? companies.get('list') : companies?.list;
    return list;
};

export const getRedirectUrl = createSelector(
  selectAuthUser,
  getCurrentCompanies,
  (user, companies) => {
    if (!user) return null;

    let hasCompanies = false;
    if (List.isList(companies)) hasCompanies = companies.size > 0;
    else if (Array.isArray(companies)) hasCompanies = companies.length > 0;
    
    const isAdmin = 
      (user.roles && user.roles.includes(ADMIN_PERMISSION)) || 
      user.permission === ADMIN_PERMISSION ||
      (user.roles?.length === 0 && !hasCompanies); 

    if (!hasCompanies) {
      return isAdmin ? '/account/type' : '/default'; 
    }

    return null; 
  }
);